import React, { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/Home")({
  component: Home,
});

export function Home() {
  const navigate = useNavigate();
  const [isAvailable, setIsAvailable] = useState(false);

  const [commits, setCommits] = useState<string[]>([]); // Stores dates of recent commits
  const GITHUB_USERNAME = "joshflores"; 

  useEffect(() => {
    document.title = "root@❤️:~$";

    const fetchGithubActivity = async () => {
      try {
        const response = await fetch(`https://api.github.com/users/${GITHUB_USERNAME}/events/public`);
        const data = await response.json();
        
        // Filter for PushEvents and extract dates
        const pushEvents = data
          .filter((event: any) => event.type === "PushEvent")
          .map((event: any) => event.created_at.split("T")[0]);
        
        setCommits(pushEvents);
      } catch (error) {
        console.error("Error fetching GitHub activity:", error);
      }
    };

    fetchGithubActivity();
  }, []);

  // Helper to determine color intensity
  const getContributionColor = (dateOffset: number) => {
    const day = new Date();
    day.setDate(day.getDate() - (27 - dateOffset));
    const dateStr = day.toISOString().split("T")[0];
    
    const count = commits.filter(d => d === dateStr).length;
    
    if (count === 0) return "#1a1a1a";
    if (count === 1) return "#0e4429";
    if (count === 2) return "#006d32";
    if (count >= 3) return "#26a641";
    return "#39d353";
  };

  // Function to check availability in EST
  const checkAvailability = () => {
    const now = new Date();
    // Format current time to EST string
    const estTimeStr = now.toLocaleString("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      hour12: false,
    });
    
    const hour = parseInt(estTimeStr, 10);
    // Available between 10 (10am) and 17 (5pm)
    setIsAvailable(hour >= 10 && hour < 17);
  };

  useEffect(() => {
    checkAvailability();
    
    // Animation Logic
    const cards = document.querySelectorAll(".card");
    cards.forEach((card, index) => {
      const htmlCard = card as HTMLElement;
      htmlCard.style.transform = "translateY(20px)";
      htmlCard.style.animationDelay = `${index * 0.1}s`;
    });
  }, []);

  const openProject = (type: string) => navigate({ to: "/Portfolio" });

  const openLink = (type: string) => {
    const externalLinks: Record<string, string> = {
      github: "https://github.com/joshflores",
      twitter: "https://twitter.com/joshfdev",
      linkedin: "https://linkedin.com/in/josh-flores",
      email: "mailto:joshflor07@gmail.com",
    };

    if (type === "portfolio") {
      navigate({ to: "/Portfolio" });
      return;
    }

    const url = externalLinks[type];
    if (url) {
      if (type === "email") {
        window.location.href = url;
      } else {
        window.open(url, "_blank");
      }
    }
  };

  return (
    <div className="container">
      {/* ... Tech Stack Card ... */}
      <div className="card tech-stack">
        <h2 className="tech-title">TECH STACK</h2>
        <div className="tech-section">
          <h3>Frontend:</h3>
          <div className="tech-items">
            {["React", "Svelte", "TypeScript", "HTML", "CSS"].map(t => <span key={t} className="tech-item">{t}</span>)}
          </div>
        </div>
        <div className="tech-section">
          <h3>Backend:</h3>
          <div className="tech-items">
            {["C#", "SQL Server", "PostgreSQL", "Node.js"].map(t => <span key={t} className="tech-item">{t}</span>)}
          </div>
        </div>
        <div className="tech-section">
          <h3>Tools:</h3>
          <div className="tech-items">
            {["Docker", "Azure", "Vercel", "Figma"].map(t => <span key={t} className="tech-item">{t}</span>)}
          </div>
        </div>
      </div>

      <div className="card profile-card">
        <div className="profile-avatar">JF</div>
        <div className="profile-info">
          <h2>Joshua Flores</h2>
          <div className="profile-handle">/in/Josh-Flores</div>
          <div className="profile-desc">I build products & experiences.</div>
        </div>
        {/* Dynamic Status Badge */}
        <div className={`status-badge ${isAvailable ? "available" : "unavailable"}`} 
             style={{ backgroundColor: isAvailable ? "#22c55e20" : "#ef444420", color: isAvailable ? "#22c55e" : "#ef4444" }}>
          {isAvailable ? "Awake Until 5PM EST" : "Asleep Until 10AM EST"}
        </div>
      </div>

      <div className="card links-card">
        <h3 className="links-title">LINKS</h3>
        <div className="social-grid">
          {["github", "linkedin", "email", "portfolio"].map(type => (
            <button key={type} className={`social-btn ${type}`} onClick={() => openLink(type)}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="card stats-card" style={{
        padding: "24px",
        background: "#111",
        border: "1px solid #222",
        minHeight: "260px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        <h3 style={{ fontSize: "12px", color: "#fff", marginBottom: "15px", letterSpacing: "1px" }}>
          CONTRIBUTION_GRAPH
        </h3>
        
        <div style={{ 
          filter: "invert(1) hue-rotate(180deg) brightness(0.8)", // Makes the white background dark/transparent-ish
          overflow: "hidden",
          borderRadius: "8px"
        }}>
          <img 
            src={`https://ghchart.rshah.org/3b82f6/${GITHUB_USERNAME}`} 
            alt="Github Contributions" 
            style={{ width: "100%", height: "auto" }}
          />
        </div>

        <div style={{ marginTop: "20px", fontSize: "11px", color: "#444", fontFamily: "monospace" }}>
          SOURCE: GITHUB_API_V3 // RECENT_ACTIVITY
        </div>
      </div>

      {/* ... Tools Card ... */}
      <div className="card tools-card">
        <h3 className="tools-title">DAILY TOOLS</h3>
        {[{n: "VS Code", i: "S", c: "vscode"}, {n: "Figma", i: "F", c: "figma"}, {n: "Notion", i: "N", c: "notion"}, {n: "Chrome", i: "C", c: "chrome"}].map(tool => (
          <div key={tool.n} className="tool-item">
            <div className={`tool-icon ${tool.c}`}>{tool.i}</div>
            <div className="tool-info"><h4>{tool.n}</h4><p>{tool.n === "Figma" ? "Design" : tool.n === "Notion" ? "Docs" : "Editor"}</p></div>
          </div>
        ))}
      </div>

      {/* Dynamic Status Bottom Card */}
      <div className="card status-card">
        <div className="status-info">
          <div className="status-dot" style={{ backgroundColor: isAvailable ? "#22c55e" : "#ef4444" }}></div>
          <span className="status-text">{isAvailable ? "Available for work" : "Away / Working / Sleeping"}</span>
        </div>
        <div className="last-updated">Full/Part-time Positions or Freelance</div>
      </div>
    </div>
  );
}

export default Home;